import React, { useEffect, useState } from 'react';
import { Button, Cell, DialogContainer } from 'react-md';
import { Autocomplete, TextField, Grid } from '@mui/material';
import ApiRequestService from './ApiRequestService';
import LoadingSpinner from './LoadingSpinner';
import { BeatLoader } from 'react-spinners';
import { NotificationManager } from 'react-notifications';

const BulkDuplicateDialog = ({ visible, onHide, selectedQuestions, currentSubjectId, currentTopicId, currentCategoryId, currentSubCategoryId, onSuccess, isSingleDuplication }) => {

    const [loading, setLoading] = useState(false);

    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);

    const loadSubjects = () => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubjects',
            data: {}
        }).then(res => {
            if (res?.data?.data?.subject_names) {
                const list = res.data.data.subject_names.map(s => ({
                    id: s.subject_id,
                    label: s.subject_name,
                    data: s,
                }));
                setSubjects(list);
                // Pre-select current subject
                const current = list.find(s => s.id === currentSubjectId);
                if (current) setSelectedSubject(current);
            }
        });
    };

    const loadTopics = (subjectId, preselect = false) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllTopics',
            data: { subject_id: subjectId }
        }).then(res => {
            if (res?.data?.data?.topic_names) {
                const list = res.data.data.topic_names.map(t => ({
                    id: t.topic_id,
                    label: t.topic_name,
                    data: t,
                }));
                setTopics(list);
                if (preselect) {
                    const current = list.find(t => t.id === currentTopicId);
                    if (current) setSelectedTopic(current);
                }
            }
        });
    };

    const loadCategories = (subjectId, topicId, preselect = false) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllCategories',
            data: { subject_id: subjectId, topic_id: topicId }
        }).then(res => {
            if (res?.data?.data?.category_names) {
                const list = res.data.data.category_names.map(c => ({
                    id: c.category_id,
                    label: c.category_name,
                    data: c,
                }));
                setCategories(list);
                if (preselect) {
                    const current = list.find(c => c.id === currentCategoryId);
                    if (current) setSelectedCategory(current);
                }
            }
        });
    };

    const loadSubcategories = (subjectId, topicId, categoryId, preselect = false) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubCategories',
            data: { subject_id: subjectId, topic_id: topicId, category_id: categoryId }
        }).then(res => {
            if (res?.data?.data?.subCategory_names) {
                const list = res.data.data.subCategory_names.map(sc => ({
                    id: sc.subcategory_id,
                    label: sc.subcategory_name,
                    data: sc,
                }));
                setSubcategories(list);
                if (preselect) {
                    const current = list.find(sc => sc.id === currentSubCategoryId);
                    if (current) setSelectedSubCategory(current);
                }
            }
        });
    };

    // Initialize when dialog opens
    useEffect(() => {
        if (visible) {
            setLoading(false);
            setSelectedSubject(null);
            setSelectedTopic(null);
            setSelectedCategory(null);
            setSelectedSubCategory(null);
            setTopics([]);
            setCategories([]);
            setSubcategories([]);

            loadSubjects();
            if (currentSubjectId) loadTopics(currentSubjectId, true);
            if (currentSubjectId && currentTopicId) loadCategories(currentSubjectId, currentTopicId, true);
            if (currentSubjectId && currentTopicId && currentCategoryId) loadSubcategories(currentSubjectId, currentTopicId, currentCategoryId, true);
        }
    }, [visible]);

    const handleSubjectChange = (_, value) => {
        setSelectedSubject(value);
        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubCategory(null);
        setTopics([]);
        setCategories([]);
        setSubcategories([]);
        if (value) loadTopics(value.id);
    };

    const handleTopicChange = (_, value) => {
        setSelectedTopic(value);
        setSelectedCategory(null);
        setSelectedSubCategory(null);
        setCategories([]);
        setSubcategories([]);
        if (value && selectedSubject) loadCategories(selectedSubject.id, value.id);
    };

    const handleCategoryChange = (_, value) => {
        setSelectedCategory(value);
        setSelectedSubCategory(null);
        setSubcategories([]);
        if (value && selectedSubject && selectedTopic) loadSubcategories(selectedSubject.id, selectedTopic.id, value.id);
    };

    const handleSubCategoryChange = (_, value) => {
        setSelectedSubCategory(value);
    };

    const onDuplicateAll = () => {
        setLoading(true);
        let payloadArr = [];

        selectedQuestions.forEach(quest => {
            payloadArr.push({
                // source question
                source_question_subject_id: quest.subject_id,
                source_question_topic_id: quest.topic_id,
                source_question_category_id: quest.category_id,
                source_question_subcategory_id: quest.subcategory_id,
                source_question_id: quest.question_id,

                // targets
                subject_id: selectedSubject.id,
                topic_id: selectedTopic.id,
                category_id: selectedCategory.id,
                subcategory_id: selectedSubCategory.id,
                question_name: quest.question_name,
            });
        });


        ApiRequestService({
            method: 'post',
            url: '/duplicateQuestions',
            data: payloadArr,
        }).then(res => {
            console.debug("res>>??", res)
            if (res && res.data) {
                if (res.data.message === '') {
                    NotificationManager.success("", "Successfully Duplicated")
                    onHide();
                    onSuccess(selectedSubject.id, selectedTopic.id, selectedCategory.id, selectedSubCategory.id);
                } else {
                    NotificationManager.error(res.data.message, "Error")
                }
            }
        }).finally(() => {
            setLoading(false);
        });
    };

    const actions = [];
    actions.push({ secondary: true, children: 'Close', onClick: onHide });
    actions.push(
        <Button
            raised
            primary
            onClick={onDuplicateAll}
            disabled={!selectedSubject || !selectedTopic || !selectedCategory || !selectedSubCategory || loading}
        >
            Duplicate
        </Button>
    );

    return (
        <DialogContainer
            id="bulk-duplicate-dialog"
            title={
                isSingleDuplication && selectedQuestions && selectedQuestions[0] ? "Duplicate question: " + selectedQuestions[0].question_name : `Duplicate ${selectedQuestions?.length || 0} Selected Question(s)`
            }
            visible={visible}
            onHide={onHide}
            contentClassName="md-grid"
            aria-describedby="bulk-duplicate-instructions"
            modal
            width={700}
            actions={actions}
        >
            {loading ? (
                <div style={{display:"flex", justifyContent:"center", alignItems:"center", width:'100%'}}>
                    <BeatLoader color={'#bb0000'} loading={loading} size={15} />
                </div>
            ) : (
                <React.Fragment>
                    <div id="bulk-duplicate-instructions" style={{ textAlign: 'left', marginBottom: '12px' }}>
                        Select the destination subject, topic, category, and subcategory to duplicate the selected {selectedQuestions?.length > 1 ? "questions": "question"}.
                    </div>
                    <Grid container spacing={2} className='mt-1'>
                        <Grid item xs={6}>
                            <Autocomplete
                                disablePortal
                                size="small"
                                options={subjects}
                                value={selectedSubject}
                                onChange={handleSubjectChange}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Subject"
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { borderRadius: "6px" },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                disablePortal
                                size="small"
                                options={topics}
                                value={selectedTopic}
                                onChange={handleTopicChange}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                disabled={!selectedSubject}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Topic"
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { borderRadius: "6px" },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                disablePortal
                                size="small"
                                options={categories}
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                disabled={!selectedTopic}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Category"
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { borderRadius: "6px" },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                disablePortal
                                size="small"
                                options={subcategories}
                                value={selectedSubCategory}
                                onChange={handleSubCategoryChange}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                disabled={!selectedCategory}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Subcategory"
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { borderRadius: "6px" },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </React.Fragment>
            )}
        </DialogContainer>
    );
};

export default BulkDuplicateDialog;
